import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface VoucherCodeCardProps {
  code: string;
  productName: string;
  productItem: string;
  onCopy?: () => void;
}

/**
 * Prominent display for voucher redeem codes returned by VocaGame (sn field).
 * Mono font, generous spacing, one-tap copy — voucher codes are the product
 * so they must be impossible to miss.
 */
const VoucherCodeCard: React.FC<VoucherCodeCardProps> = ({
  code,
  productName,
  productItem,
  onCopy,
}) => {
  const { t } = useTranslation('voucher_card');
  const [copied, setCopied] = useState(false);

  // Translate the parsed-field labels at render time so locale flips
  // pick up new copy without re-running the regex.
  const parsedRaw = parseVoucher(code);
  const parsed = parsedRaw?.map((p) => ({
    ...p,
    label:
      p.key === 'code'
        ? t('redeem_code_label')
        : p.key === 'pin'
        ? t('pin_label')
        : t('serial_label'),
  }));
  const primaryCode = parsed?.find((p) => p.key === 'code' || p.key === 'pin')?.value ?? code;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(primaryCode);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const redemptionHint = getRedemptionHint(productName);

  return (
    <div className="mt-4 text-left rounded-xl overflow-hidden border-2 border-primary/30 shadow-sm">
      {/* Header */}
      <div className="bg-primary/10 px-4 py-2.5 border-b border-primary/20 flex items-center gap-2">
        <svg viewBox="0 0 24 24" className="w-4 h-4 text-primary" fill="currentColor">
          <path d="M4 4h16a2 2 0 012 2v2.5a1.5 1.5 0 000 3V14a2 2 0 01-2 2H4a2 2 0 01-2-2v-2.5a1.5 1.5 0 000-3V6a2 2 0 012-2zm0 2v2.5a3.5 3.5 0 000 7V14h16v-2.5a3.5 3.5 0 000-7V6H4zm4 3h8v2H8V9zm0 4h8v2H8v-2z" />
        </svg>
        <span className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">
          {t('your_code')}
        </span>
      </div>

      {/* Code + copy */}
      <div className="bg-white px-4 py-5">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
          {productName} · {productItem}
        </div>
        {parsed ? (
          <div className="space-y-2">
            {parsed.map((p, i) => {
              const isPrimary = p.key === 'code' || p.key === 'pin';
              return (
                <div key={i} className="flex items-stretch gap-2">
                  <div
                    className={`flex-1 rounded-lg border px-3 py-3 font-mono break-all select-all ${
                      isPrimary
                        ? 'bg-gray-50 border-gray-200 text-base sm:text-lg text-gray-900'
                        : 'bg-white border-gray-200 text-sm text-gray-600'
                    }`}
                    aria-label={p.label.toLowerCase()}
                  >
                    <span className={`text-[10px] uppercase tracking-wider block mb-0.5 ${isPrimary ? 'text-primary font-semibold' : 'text-gray-400'}`}>
                      {p.label}
                      {!isPrimary && <span className="normal-case tracking-normal text-gray-400"> · {t('reference_only')}</span>}
                    </span>
                    {p.value}
                  </div>
                  {isPrimary && (
                    <button
                      onClick={handleCopy}
                      className={`px-4 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${
                        copied
                          ? 'bg-green-600 text-white'
                          : 'bg-primary text-white hover:brightness-90'
                      }`}
                      aria-label={t('copy_aria')}
                    >
                      {copied ? t('copied') : t('copy')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-stretch gap-2">
            <div
              className="flex-1 rounded-lg bg-gray-50 border border-gray-200 px-3 py-3 font-mono text-base sm:text-lg text-gray-900 break-all select-all"
              aria-label={t('voucher_code_aria')}
            >
              {code}
            </div>
            <button
              onClick={handleCopy}
              className={`px-4 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${
                copied
                  ? 'bg-green-600 text-white'
                  : 'bg-primary text-white hover:brightness-90'
              }`}
              aria-label="copy voucher code"
            >
              {copied ? t('copied') : t('copy')}
            </button>
          </div>
        )}

        {/* Redemption guide */}
        {redemptionHint && (
          <div className="mt-3 text-xs text-gray-600 bg-blue-50/50 border border-blue-200 rounded-lg p-3">
            <p className="font-semibold text-blue-900 mb-1">{t('how_to_redeem')}</p>
            <p className="text-blue-900/80 leading-snug">{redemptionHint}</p>
          </div>
        )}

        {/* Safety note */}
        <p className="mt-3 text-[11px] text-gray-500">
          {t('save_immediately')}
        </p>
      </div>
    </div>
  );
};

type ParsedField = { key: 'code' | 'pin' | 'serial'; label: string; value: string };

function parseVoucher(raw: string): ParsedField[] | null {
  // Two provider shapes are in play:
  //   VocaGame: "Code: ABC  SN: 123"          (colon-separated, 2+ fields)
  //   Uniplay:  "AccountNumber=4HHNT1FVZ20GV96A" (equals-separated, often 1)
  // Without the second pattern the Uniplay code fell through to the raw
  // fallback, so the customer saw — and the copy button copied — the whole
  // "AccountNumber=..." string, which fails when pasted into a redeem form.
  // The value stops at whitespace or a pair separator, so a multi-field string
  // like "AccountNumber=AAA&PIN=1234" yields two fields instead of swallowing
  // the rest into the first value.
  const pattern = /(Account\s*Number|Code|PIN|Serial|SN)\s*[:=]\s*([^\s&;,|]+)/gi;
  const out: ParsedField[] = [];
  for (const m of raw.matchAll(pattern)) {
    const tag = m[1].toUpperCase().replace(/\s+/g, '');
    if (tag === 'CODE' || tag === 'ACCOUNTNUMBER') {
      out.push({ key: 'code', label: 'Redeem Code', value: m[2] });
    } else if (tag === 'PIN') {
      out.push({ key: 'pin', label: 'PIN', value: m[2] });
    } else {
      out.push({ key: 'serial', label: 'Serial Number', value: m[2] });
    }
  }
  // A single labelled field is still worth rendering: it strips the label from
  // the copied value, which is the whole point for one-field providers.
  return out.length >= 1 ? out : null;
}

// Platform-specific redemption guides. Falls back to generic advice.
function getRedemptionHint(productName: string): string {
  const n = productName.toLowerCase();
  if (n.includes('spotify')) {
    return 'Go to spotify.com/redeem, sign in, paste the code, and confirm.';
  }
  if (n.includes('roblox')) {
    return 'Open Roblox → Robux → Redeem Code. Paste and confirm.';
  }
  if (n.includes('steam')) {
    return 'Open Steam → Games → Redeem a Steam Wallet Code. Paste and confirm.';
  }
  if (n.includes('google play') || n.includes('googleplay')) {
    return 'Open Google Play Store → Menu → Redeem. Paste the code.';
  }
  if (n.includes('apple') || n.includes('itunes')) {
    return 'Open App Store → Profile → Redeem Gift Card or Code. Paste the code.';
  }
  if (n.includes('netflix')) {
    return 'Go to netflix.com/redeem, sign in, and paste the code.';
  }
  if (n.includes('garena')) {
    return 'Open Garena app → Wallet → Top-up → Redeem Code.';
  }
  if (n.includes('mi cash') || n.includes('megaxus')) {
    return 'Open Megaxus portal → Top-up → Mi-Cash Voucher. Paste the code.';
  }
  return 'Open the service app or website and find the redeem / voucher section. Paste the code above.';
}

export default VoucherCodeCard;
